import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { Button, Chip, CircularProgress, IconButton, LinearProgress, Slider, Switch, Tooltip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LinkIcon from "@mui/icons-material/Link";

import { useSnackbar } from "../components/AlertNotification";
import HostCaptureBar from "../components/HostCaptureBar";
import LiveBackgroundModal from "../components/LiveBackgroundModal";
import LiveLinkModal from "../components/LiveLinkModal";
import ClassicLiveStage from "../components/ClassicLiveStage";
import {
  segmentsSentenceCount,
  segmentText,
  turnAtSentence,
  turnEndSentence,
  useTapestryState,
} from "../vendor/liveTapRenderer";
import BoardLiveView from "../components/BoardLiveView";
import SceneLiveView from "../components/SceneLiveView";
import { createAppbackResourceAdapter } from "../liveTap/appbackResourceAdapter";
import { boardFeed, createOwnerFeedClient, plansItself } from "../liveTap/liveFeedClient";
import { TOOL_BTN_SX, TOOL_BTN_ACTIVE_SX, statusTone } from "../liveTap/liveChrome";
import { LIVE_SYSTEMS, boardSystem, readLiveSystem, writeLiveSystem, liveSystemInfo } from "../liveTap/liveSystem";
import { RootContext } from "../context/root-provider";
import { usePageHeader } from "../context/PageHeaderContext";
import { useDropAnything } from "../context/DropAnythingContext";
import useLiveTranscript from "../hooks/useLiveTranscript";
import useCassetteRequest from "../hooks/useCassetteRequest";
import useSymbolName from "../hooks/useSymbolName";
import styles from "./LiveTapPage.module.scss";

// Annotators the dev control can run a new replay cassette with (the
// platform's catalog names).
const REPLAY_ANNOTATORS = ["symbolizer", "meeting_sme"];

// How far the transport arrows move, in sentences. Deliberately NOT one display
// turn (TT-971): turns are runs of the same speaker, so they run from a single
// sentence to the entire meeting. Stepping by turn measured 3–19% of a meeting
// per press on multi-speaker transcripts, did nothing at all on single-speaker
// ones (one turn ⇒ every arrow disabled), and couldn't reach inside the first
// turn at all. A constant stride is uniform however the transcript happens to
// be labeled.
const SENTENCE_STEP = 10;

// Transport buttons need an explicit disabled color: MUI's default disabled
// tint is nearly invisible against the dark page background in dark mode.
const TRANSPORT_BTN_SX = {
  color: "var(--color-text-secondary)",
  "&.Mui-disabled": { color: "var(--color-text-muted)" },
};

const TABS = ["live", "workbench", "transcript", "symbols"];
const TAB_LABELS = {
  live: "Live Tap",
  workbench: "Workbench",
  transcript: "Transcript",
  symbols: "Symbols",
};

function shortId(id) {
  return id ? `${id.slice(0, 8)}...` : "";
}

function formatSeconds(ms) {
  return Number.isFinite(ms) ? `${(ms / 1000).toFixed(1)}s` : "–";
}

function normalizeSentence(sentence) {
  if (typeof sentence === "string") {
    return { text: sentence, score: 0, symbolIds: [] };
  }

  return {
    text: sentence?.text || "",
    score: Number(sentence?.displayScore ?? sentence?.display_score ?? 0),
    symbolIds: Array.isArray(sentence?.symbol_ids) ? sentence.symbol_ids : [],
    reasoning: sentence?.reasoning || "",
  };
}

function segmentSeq(segment, fallback) {
  return Number(segment?.seqnum ?? segment?.seqNum ?? fallback);
}

function scoreTone(score) {
  if (score >= 0.8) {
    return styles.scoreHigh;
  }
  if (score >= 0.5) {
    return styles.scoreMid;
  }
  return styles.scoreLow;
}

const LiveTapPage = () => {
  const { docId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // Mid-meeting batch tapestry: the live cassette read once at the frontier
  // it had reached (T, as known) instead of followed (same renderer path).
  const checkpoint = searchParams.get("checkpoint") === "1";
  // A cassette other than the live one (a replay, the batch read): the dev
  // control below picks it.
  const csstParam = searchParams.get("csst");
  const navigate = useNavigate();
  const { userInfo, loggingIn } = useContext(RootContext);
  const { setHeaderContent } = usePageHeader();
  const { showSnackbar, snackbarEl } = useSnackbar();
  // A notice the start modal handed over with the navigation (TT-1190): shown
  // once, then dropped from history so a reload doesn't repeat it.
  const location = useLocation();
  const notice = location.state?.notice;
  useEffect(() => {
    if (!notice?.message) return;
    showSnackbar(notice.message, notice.severity || "info");
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, navigate, notice, showSnackbar]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [autoFollow, setAutoFollow] = useState(true);
  const [threshold, setThreshold] = useState(0.5);
  // The playhead is a SENTENCE ordinal (TT-971), not a turn index: turns are
  // runs of the same speaker, so a live meeting keeps growing INSIDE its last
  // turn — at per-utterance cadence a single-speaker meeting is one giant turn,
  // which is why pausing on a turn index paused nothing. The transport below
  // navigates in the same unit (SENTENCE_STEP). -1 until the first poll lands.
  const [currentSent, setCurrentSent] = useState(-1);
  const [tab, setTab] = useState("live");
  const tapestryRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  // Manual background selection (TT-962): the renderer owns the pin but we drive
  // it from the palette button + picker modal, mirroring the eligible catalog the
  // renderer publishes.
  const [forcedBackgroundId, setForcedBackgroundId] = useState(null);
  const [eligibleBackgrounds, setEligibleBackgrounds] = useState([]);
  const [bgModalOpen, setBgModalOpen] = useState(false);
  // Public view-only links (TT-1110): mint / copy / revoke from the host page.
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  // Info side panel (TT-972): the primary symbol's description + the transcript,
  // beside the canvas. The toolbar owns the toggle; ClassicLiveStage owns the
  // rest (which background is painted, its description, the exit timer).
  const [infoOpen, setInfoOpen] = useState(false);
  // Which rendering system draws the live view (TT-1046): classic (curated
  // tapestry) or scene (server-built graphic recording board). Chosen in the
  // Live Tapestry modal, persisted per doc in localStorage, and flippable here
  // so an already-open tap can switch too. Modeled as a list, not a boolean —
  // a third experimental system is expected.
  const [liveSystem, setLiveSystem] = useState(() => readLiveSystem(docId));
  useEffect(() => {
    setLiveSystem(readLiveSystem(docId));
  }, [docId]);
  const handleLiveSystemChange = useCallback(
    (event) => {
      const value = event.target.value;
      setLiveSystem(value);
      writeLiveSystem(docId, value);
    },
    [docId]
  );

  // Dev/workbench escape hatch. Hidden by default for the client-facing view;
  // Ctrl/Cmd+I toggles it to reveal metrics, the doc id, the tabs, the advance
  // strip, and the renderer's debug panel.
  const [devMode, setDevMode] = useState(false);

  useEffect(() => {
    const onKey = (event) => {
      if ((event.ctrlKey || event.metaKey) && (event.key === "i" || event.key === "I")) {
        event.preventDefault();
        setDevMode((value) => !value);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const userId = userInfo?.username;
  // The host reads the meeting through the owner feed client; a public
  // viewer reads the same views through a link client (TT-1110).
  const feed = useMemo(() => createOwnerFeedClient(userId, docId), [userId, docId]);
  // A doc's v2 board keeps the archetype it was created with, so once one
  // exists only its own system can draw it: the other v2 option is not
  // offered (TT-1150). A mounted v2 view reports the board it polls; on
  // classic nothing polls it, so the page reads it once itself.
  const [board, setBoard] = useState(null);
  useEffect(() => {
    if (!feed || liveSystem !== "classic") return undefined;
    let live = true;
    feed.getSceneBoard(0).then((r) => {
      if (live && r && !r.error) setBoard(r);
    });
    return () => {
      live = false;
    };
  }, [feed, liveSystem]);
  const boardOwner = boardSystem(board);
  const system = liveSystemInfo(liveSystem);
  const offeredSystems = LIVE_SYSTEMS.filter((s) => s.id === "classic" || !boardOwner || s.id === boardOwner);
  const {
    read,
    title: docTitle,
    createdAt,
    cassette,
    segments,
    telemetry,
    status,
    done,
    seenLagMs,
    loading,
    refreshing,
    error,
    refresh,
  } = useLiveTranscript(feed, {
    // Client view is always live; the Live toggle only applies in dev mode.
    autoRefresh: devMode ? autoRefresh : true,
    follow: !checkpoint,
    csstId: csstParam,
    // the workbench's engine readouts are the cassette's telemetry
    telemetry: devMode,
  });
  const docDate = useMemo(() => {
    const created = createdAt ? dayjs(createdAt) : null;
    return created?.isValid() ? created.format("M/D/YYYY") : "";
  }, [createdAt]);
  // The checkpoint view is a snapshot at the frontier it opened on, not a
  // live tap: for the page's chrome and the board views it is as done (no
  // drop stand-down, no board poll past its first read, no advance).
  const ended = done || checkpoint;
  // The board is the live cassette's plan: its views advance it only while
  // that cassette does not plan itself. The flag is asked at each advance
  // (a ref), so a doc re-read never gives the views a new feed: a new feed
  // restarts the board poll and drops the versions it holds.
  const selfPlanningRef = useRef(false);
  selfPlanningRef.current = plansItself((read?.cassettes || []).find((c) => c.kind === "live"));
  const viewFeed = useMemo(() => boardFeed(feed, () => selfPlanningRef.current), [feed]);
  // While this tap is live, a link dropped on the page must not start a
  // second one over it (TT-1231); the app-wide drop stands down. A cassette
  // not yet read counts as live, as the page's own label does; a feed that
  // failed is no live tap, so it does not stand the drop down.
  const { setLiveConflict } = useDropAnything();
  useEffect(() => {
    setLiveConflict(!error && !ended);
    return () => setLiveConflict(false);
  }, [ended, error, setLiveConflict]);
  // Workbench/Symbols panels read the SAME vendored reducer the canvas uses —
  // the page's old local mini-reducer (turn-stepped, no recency) is gone
  // (TT-903 render convergence).
  const tapestry = useTapestryState(segments, currentSent, threshold);

  // Dev control: a new replay cassette of this doc with the chosen
  // annotator (the platform runs it as if live, beside the live one); the
  // cassette picker then follows it.
  const [replayAnnotator, setReplayAnnotator] = useState("symbolizer");
  const replay = useCassetteRequest(feed);
  const followCassette = useCallback(
    (id) => {
      const next = new URLSearchParams(searchParams);
      if (id) next.set("csst", id);
      else next.delete("csst");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Vendored Live Tap renderer wiring: appback resource adapter. The feed is
  // already on the renderer's snake_case contract, so segments pass straight
  // through (no front-end casing adapter).
  const resources = useMemo(() => createAppbackResourceAdapter(), []);
  // The dev panels and the background picker hold symbols by symbol_id and
  // show their names.
  const nameOf = useSymbolName(resources);

  // Sentences are also the honest progress unit for the readouts (TT-942
  // finding 1) — a turn count freezes at "2/2" while content keeps growing.
  const totalSentences = useMemo(() => segmentsSentenceCount(segments), [segments]);
  // The turn containing the playhead — for turn-scoped chrome only (the
  // workbench's current-segment readout, the transcript row highlight). The
  // transport arrows never step by turn; the transcript rows still jump by one.
  const currentTurn = useMemo(() => turnAtSentence(segments, currentSent), [segments, currentSent]);
  // The `segments` guards cover the render between a doc switch emptying the
  // feed and the playhead effect resetting `currentSent` to -1.
  const atStart = !segments.length || currentSent <= 0;
  const atEnd = !segments.length || currentSent >= totalSentences - 1;

  const currentSegment = segments[currentTurn] || null;
  // The cassette's engine state (TT-1103), from the doc read's telemetry.
  const lastDripNum = telemetry?.last_drip_num ?? 0;
  const nextSeq = telemetry?.next_seq ?? 0;
  const rawTail = telemetry?.raw_tail || "";
  // The spine's last pass (TT-1103): what it committed and what it cost.
  const lastPass = telemetry?.last_pass || null;
  const title = docTitle || "Live Tap";
  // Outside dev mode the page only ever shows the tapestry, regardless of which
  // tab was last selected.
  const activeTab = devMode ? tab : "live";

  // Keyed on the sentence total, not segments.length — on a single-speaker
  // meeting the turn count never moves, so following it followed nothing.
  useEffect(() => {
    if (!totalSentences) {
      setCurrentSent(-1);
      return;
    }

    setCurrentSent((sent) => {
      if (autoFollow) {
        return totalSentences - 1;
      }
      return Math.min(sent, totalSentences - 1);
    });
  }, [autoFollow, totalSentences]);

  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  const handleFirst = useCallback(() => {
    setAutoFollow(false);
    setCurrentSent(0);
  }, []);

  const handlePrev = useCallback(() => {
    setAutoFollow(false);
    setCurrentSent((sent) => Math.max(sent - SENTENCE_STEP, 0));
  }, []);

  // Toggle live auto-follow. Enabling it lets the playhead effect snap to the
  // latest sentence as content arrives; disabling it pins the current one.
  const handleToggleFollow = useCallback(() => {
    setAutoFollow((value) => !value);
  }, []);

  const handleNext = useCallback(() => {
    // Stepping onto the live edge re-arms auto-follow, so walking forward off
    // the end resumes rather than pinning one sentence behind.
    const next = Math.min(currentSent + SENTENCE_STEP, totalSentences - 1);
    setAutoFollow(next >= totalSentences - 1);
    setCurrentSent(next);
  }, [currentSent, totalSentences]);

  // Re-arming follow is enough to jump: the playhead effect owns where the
  // playhead goes while following, and setting it here too would be a second
  // copy of that rule (the play button already relies on the effect alone).
  const handleLatest = useCallback(() => setAutoFollow(true), []);

  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await tapestryRef.current?.exportPng();
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  const sentencesThrough = currentSent + 1;
  const progress = totalSentences ? (sentencesThrough / totalSentences) * 100 : 0;

  const statusLabel = useMemo(() => {
    if (checkpoint) {
      return "checkpoint";
    }
    if (refreshing && !loading) {
      return "syncing";
    }
    return status || "running";
  }, [checkpoint, loading, refreshing, status]);

  useEffect(() => {
    setHeaderContent(
      <div className={styles.navHeaderContent}>
        <Tooltip title="Documents">
          <IconButton className={styles.navIconBtn} onClick={() => navigate("/")}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <div className={styles.navTitleBlock}>
          <span className={styles.navTitle}>Live Tap</span>
        </div>
        <Chip size="small" color={statusTone(status)} label={statusLabel} />
        {devMode && (
          <Tooltip title="Refresh">
            <span>
              <IconButton className={styles.navIconBtn} onClick={handleRefresh} disabled={refreshing}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </div>
    );

    return () => setHeaderContent(null);
  }, [devMode, handleRefresh, navigate, refreshing, setHeaderContent, status, statusLabel]);

  if (!docId) {
    return <Navigate to="/" replace />;
  }

  if (loggingIn || !userId) {
    return (
      <div className={styles.pageContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <main className={styles.shell}>
        <section className={styles.statusBand}>
          <div className={styles.statusTitle}>
            <h1>{title}</h1>
            {devMode && <span className={styles.docId}>Doc {shortId(docId)}</span>}
          </div>

          <div className={styles.controlBand}>
            <div className={styles.transport}>
              <TransportButton
                title="Back to the start"
                onClick={handleFirst}
                disabled={atStart}
              >
                <SkipPreviousIcon />
              </TransportButton>
              <TransportButton
                title={`Back ${SENTENCE_STEP} sentences`}
                onClick={handlePrev}
                disabled={atStart}
              >
                <NavigateBeforeIcon />
              </TransportButton>
              <TransportButton
                title={autoFollow ? "Pause auto-follow" : "Follow latest"}
                onClick={handleToggleFollow}
                disabled={!segments.length}
              >
                {autoFollow ? <PauseIcon /> : <PlayArrowIcon />}
              </TransportButton>
              <TransportButton
                title={`Forward ${SENTENCE_STEP} sentences`}
                onClick={handleNext}
                disabled={atEnd}
              >
                <NavigateNextIcon />
              </TransportButton>
              <TransportButton
                title="Jump to live"
                onClick={handleLatest}
                disabled={atEnd}
              >
                <SkipNextIcon />
              </TransportButton>
              <span className={styles.segmentIndex}>
                {totalSentences ? `${sentencesThrough} / ${totalSentences} sentences` : "0 / 0"}
              </span>
              {/* The palette pins a classic background — it stands down while
                  the scene or board system is drawing (TT-1046). */}
              {activeTab === "live" && liveSystem === "classic" && (
                <Tooltip title="Choose the background">
                  <span>
                    <IconButton
                      onClick={() => setBgModalOpen(true)}
                      disabled={!segments.length}
                      sx={forcedBackgroundId ? TOOL_BTN_ACTIVE_SX : TOOL_BTN_SX}
                    >
                      <PaletteOutlinedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {/* The transcript panel and the PNG export, for the systems
                  that have them (liveSystem.js). */}
              {activeTab === "live" && system.panel && (
                <>
                  <Tooltip title={system.panel}>
                    <span>
                      <IconButton
                        onClick={() => setInfoOpen((value) => !value)}
                        sx={{ ...(infoOpen ? TOOL_BTN_ACTIVE_SX : TOOL_BTN_SX), marginLeft: "8px" }}
                      >
                        <InfoOutlinedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Export tapestry as PNG">
                    <span>
                      <IconButton
                        onClick={handleExport}
                        disabled={exporting || !segments.length || !system.exportable}
                        sx={{ ...TOOL_BTN_SX, marginLeft: "8px" }}
                      >
                        <FileDownloadIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
              {/* Rendering-system switcher (TT-1046): temporary scaffolding so
                  an open tap can flip systems while the direction settles. */}
              {activeTab === "live" && (
                <label className={styles.systemSelect} title="Rendering system (experimental)">
                  <select value={liveSystem} onChange={handleLiveSystemChange} aria-label="Rendering system">
                    {offeredSystems.map((system) => (
                      <option key={system.id} value={system.id}>
                        {system.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {/* Public live link (TT-1110): anyone with the URL watches this
                  tapestry, read-only, with no tapp account. Not offered on a
                  checkpoint view — that is a batch tapestry, not a meeting. */}
              {activeTab === "live" && !checkpoint && (
                <Tooltip title="Share a public live link">
                  <span>
                    <IconButton
                      onClick={() => setLinkModalOpen(true)}
                      sx={{ ...TOOL_BTN_SX, marginLeft: "8px" }}
                      aria-label="Share a public live link">
                      <LinkIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </div>

            {devMode && (
              <>
                <div className={styles.sliderWrap}>
                  <span>Threshold</span>
                  <Slider
                    min={0}
                    max={1}
                    step={0.05}
                    value={threshold}
                    onChange={(_, value) => setThreshold(Array.isArray(value) ? value[0] : value)}
                    size="small"
                    sx={{ color: "var(--color-accent-primary)" }}
                  />
                  <span>{threshold.toFixed(2)}</span>
                </div>

                <label className={styles.switchControl}>
                  <Switch
                    checked={autoRefresh}
                    onChange={(event) => setAutoRefresh(event.target.checked)}
                    size="small"
                  />
                  <span>Live</span>
                </label>

                <Button
                  startIcon={<RefreshIcon />}
                  variant="outlined"
                  size="small"
                  onClick={handleRefresh}
                  disabled={refreshing}
                >
                  Refresh
                </Button>
              </>
            )}
          </div>
        </section>

        {/* This tab is the capture agent for a host-audio tapestry (TT-1102):
            the bar is the only place the capture can be muted or stopped. */}
        <HostCaptureBar docId={docId} />

        {devMode && (
          <div className={styles.metrics}>
            <Metric label="Turns" value={segments.length} />
            <Metric label="Sentences" value={totalSentences} />
            <Metric label="Last drip" value={lastDripNum} />
            <Metric label="Next seq" value={nextSeq} />
            <Metric label="Tail" value={rawTail ? `${rawTail.split(/\s+/).filter(Boolean).length} words` : "0 words"} />
          </div>
        )}

        {error && <div className={styles.errorBanner}>{error}</div>}

        {devMode && <LinearProgress variant="determinate" value={progress} className={styles.progressBar} />}

        {devMode && lastPass && (
          <div className={styles.advanceStrip}>
            {/* words → analysis, by leg: the oldest words the pass committed had
                waited `oldest_drip_age_ms` since ingest, `annotate_ms` of it in
                the annotator; `seenLagMs` is this client's read leg on top */}
            Last pass: {lastPass.drips || 0} drip{lastPass.drips === 1 ? "" : "s"} → {lastPass.sentences || 0} sentence
            {lastPass.sentences === 1 ? "" : "s"}, annotate {formatSeconds(lastPass.annotate_ms)} / pass{" "}
            {formatSeconds(lastPass.advance_ms)}
            {lastPass.oldest_drip_age_ms != null && ` / oldest drip ${formatSeconds(lastPass.oldest_drip_age_ms)} old`}
            {seenLagMs != null && ` / seen ${formatSeconds(seenLagMs)} after commit`}
            {lastPass.error && " (failed)"}
          </div>
        )}

        {devMode && (
          <div className={styles.advanceStrip}>
            {/* Which cassette the page plays: the doc read lists them. */}
            <span>Cassette:</span>{" "}
            <select
              value={cassette?.csst_id || ""}
              onChange={(e) => followCassette(e.target.value)}
              aria-label="Cassette"
            >
              {(read?.cassettes || []).map((c) => (
                <option key={c.csst_id} value={c.csst_id}>
                  {c.csst_id} · {c.kind} · {c.state || "—"}
                </option>
              ))}
            </select>{" "}
            <span>New replay with</span>{" "}
            <select
              value={replayAnnotator}
              onChange={(e) => setReplayAnnotator(e.target.value)}
              disabled={replay.pending}
              aria-label="Annotator"
            >
              {REPLAY_ANNOTATORS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>{" "}
            <Button
              size="small"
              variant="outlined"
              disabled={replay.pending || !feed?.requestCassette}
              onClick={() => replay.request({ kind: "replay", annotators: [replayAnnotator] })}
            >
              {replay.pending ? "Asking…" : "Replay"}
            </Button>{" "}
            {replay.cassette && (
              <Button size="small" onClick={() => followCassette(replay.cassette.csst_id)}>
                Follow {replay.cassette.csst_id}
              </Button>
            )}
            {replay.error && <span>{"⚠"} {replay.error}</span>}
          </div>
        )}

        {devMode && (
          <div className={styles.tabs} role="tablist" aria-label="Live tap views">
            {TABS.map((tabName) => (
              <button
                key={tabName}
                className={tab === tabName ? styles.tabActive : styles.tab}
                onClick={() => setTab(tabName)}
                type="button"
              >
                {TAB_LABELS[tabName]}
              </button>
            ))}
          </div>
        )}

        <div className={styles.viewArea}>
          {loading ? (
            <div className={styles.emptyState}>
              <CircularProgress />
              <span>Loading live tap</span>
            </div>
          ) : (
            <>
              {/* Scene system (TT-1046): the server-built graphic recording
                  board replaces the classic tapestry canvas wholesale; the
                  classic branch below renders exactly as before. */}
              {activeTab === "live" && liveSystem === "scene" && (
                <SceneLiveView
                  ref={tapestryRef}
                  feed={viewFeed}
                  resources={resources}
                  docTitle={title}
                  docDate={docDate}
                  segments={segments}
                  currentSent={currentSent}
                  currentTurn={currentTurn}
                  threshold={threshold}
                  closed={ended}
                  debug={devMode}
                  infoOpen={infoOpen}
                  onInfoOpenChange={setInfoOpen}
                  onBoard={setBoard}
                />
              )}
              {/* Board system (TT-1046 Live Tapestry v2): the plan-series
                  graphic-recording board — same swap contract as the scene
                  system above. */}
              {activeTab === "live" && liveSystem === "board" && (
                <BoardLiveView
                  ref={tapestryRef}
                  feed={viewFeed}
                  resources={resources}
                  docTitle={title}
                  docDate={docDate}
                  segments={segments}
                  currentSent={currentSent}
                  currentTurn={currentTurn}
                  threshold={threshold}
                  closed={ended}
                  debug={devMode}
                  infoOpen={infoOpen}
                  onInfoOpenChange={setInfoOpen}
                  onBoard={setBoard}
                />
              )}
              {/* The canvas and its info panel — shared with the public
                  viewer (TT-1110); the host's extras ride on canvasProps. */}
              {activeTab === "live" && liveSystem === "classic" && (
                <ClassicLiveStage
                  ref={tapestryRef}
                  segments={segments}
                  currentSent={currentSent}
                  threshold={threshold}
                  meetingName={title}
                  meetingDate={docDate}
                  resources={resources}
                  infoOpen={infoOpen}
                  canvasProps={{
                    currentTurn,
                    debugTools: devMode,
                    forcedBackgroundId,
                    onForcedBackgroundIdChange: setForcedBackgroundId,
                    onEligibleBackgroundsChange: setEligibleBackgrounds,
                  }}
                />
              )}
              {activeTab === "workbench" && (
                <TapestryPanel
                  currentSegment={currentSegment}
                  tapestry={tapestry}
                  threshold={threshold}
                  nameOf={nameOf}
                />
              )}
              {activeTab === "transcript" && (
                <TranscriptPanel
                  currentTurn={currentTurn}
                  onSelect={(idx) => {
                    setAutoFollow(false);
                    setCurrentSent(turnEndSentence(segments, idx));
                  }}
                  segments={segments}
                  threshold={threshold}
                  nameOf={nameOf}
                />
              )}
              {activeTab === "symbols" && <SymbolsPanel tapestry={tapestry} nameOf={nameOf} />}
            </>
          )}
        </div>
      </main>
      <LiveBackgroundModal
        open={bgModalOpen}
        onClose={() => setBgModalOpen(false)}
        backgrounds={eligibleBackgrounds}
        forcedBackgroundId={forcedBackgroundId}
        onSelect={setForcedBackgroundId}
        onAuto={() => setForcedBackgroundId(null)}
        nameOf={nameOf}
      />
      <LiveLinkModal
        open={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        userId={userId}
        docId={docId}
        liveSystem={liveSystem}
        docTitle={title}
      />
      {snackbarEl}
    </div>
  );
};

function Metric({ label, value }) {
  return (
    <div className={styles.metric}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function TransportButton({ title, onClick, disabled, children }) {
  return (
    <Tooltip title={title}>
      {/* span wrapper keeps the Tooltip working while the button is disabled */}
      <span>
        <IconButton onClick={onClick} disabled={disabled} sx={TRANSPORT_BTN_SX}>
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function TapestryPanel({ currentSegment, tapestry, threshold, nameOf }) {
  const visibleSymbols = tapestry.ranked
    .filter((symbolId) => tapestry.symbols[symbolId]?.role !== "hidden")
    .slice(0, 8);
  const recentSentences = (tapestry.events || []).filter((event) => event.type === "sentence.surface").slice(-3);

  return (
    <section className={styles.tapestryStage}>
      <div className={styles.symbolField}>
        {visibleSymbols.length ? (
          visibleSymbols.map((symbolId) => {
            const symbol = tapestry.symbols[symbolId];
            return (
              <div className={`${styles.symbolNode} ${styles[symbol.role]}`} key={symbolId}>
                <span className={styles.symbolName}>{nameOf(symbolId)}</span>
                <span className={styles.symbolCount}>{symbol.count}</span>
                {symbol.sentences[0]?.text && <p>{symbol.sentences[0].text}</p>}
              </div>
            );
          })
        ) : (
          <div className={styles.waitingPanel}>
            <span>Waiting for scored transcript</span>
          </div>
        )}
      </div>

      <aside className={styles.liveReadout}>
        <div className={styles.readoutHeader}>
          <span>Current Segment</span>
          <Chip size="small" label={`>= ${threshold.toFixed(2)}`} />
        </div>
        {currentSegment ? (
          <>
            <div className={styles.segmentMeta}>
              <strong>{currentSegment.speaker || `Segment ${segmentSeq(currentSegment, 0)}`}</strong>
              {currentSegment.start && <span>{currentSegment.start}</span>}
            </div>
            <div className={styles.sentences}>
              {(currentSegment.sentences || []).map((rawSentence, idx) => {
                const sentence = normalizeSentence(rawSentence);
                const scored = sentence.score >= threshold;
                return (
                  <div className={styles.sentence} key={`${segmentSeq(currentSegment, 0)}-${idx}`}>
                    <p>{sentence.text}</p>
                    {scored && (
                      <div className={styles.sentenceMeta}>
                        <span className={`${styles.scoreBadge} ${scoreTone(sentence.score)}`}>
                          {sentence.score.toFixed(2)}
                        </span>
                        {sentence.symbolIds.map((symbolId) => (
                          <span className={styles.symbolChip} key={symbolId}>
                            {nameOf(symbolId)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className={styles.waitingText}>Waiting for transcript</div>
        )}
        {!!recentSentences.length && (
          <div className={styles.recentList}>
            {recentSentences.map((sentence) => (
              <span key={`${sentence.score}-${sentence.text}`}>{sentence.text}</span>
            ))}
          </div>
        )}
      </aside>
    </section>
  );
}

function TranscriptPanel({ currentTurn, onSelect, segments, threshold, nameOf }) {
  if (!segments.length) {
    return <div className={styles.emptyState}>Waiting for transcript</div>;
  }

  return (
    <section className={styles.transcriptList}>
      {segments.map((segment, idx) => {
        const seq = segmentSeq(segment, idx);
        const active = idx === currentTurn;
        const highSymbolIds = (segment.sentences || [])
          .map(normalizeSentence)
          .filter((sentence) => sentence.score >= threshold)
          .flatMap((sentence) => sentence.symbolIds);
        const uniqueSymbolIds = [...new Set(highSymbolIds)].slice(0, 6);

        return (
          <button
            className={active ? styles.transcriptRowActive : styles.transcriptRow}
            key={seq}
            onClick={() => onSelect(idx)}
            type="button"
          >
            <div className={styles.rowMeta}>
              <strong>{segment.speaker || `Segment ${seq}`}</strong>
              <span>{segment.start || `#${seq}`}</span>
            </div>
            <p>{segmentText(segment)}</p>
            {!!uniqueSymbolIds.length && (
              <div className={styles.rowSymbols}>
                {uniqueSymbolIds.map((symbolId) => (
                  <span className={styles.symbolChip} key={symbolId}>
                    {nameOf(symbolId)}
                  </span>
                ))}
              </div>
            )}
          </button>
        );
      })}
    </section>
  );
}

function SymbolsPanel({ tapestry, nameOf }) {
  const ranked = tapestry.ranked.filter((symbolId) => tapestry.symbols[symbolId]?.role !== "hidden");

  if (!ranked.length) {
    return <div className={styles.emptyState}>Waiting for symbols</div>;
  }

  return (
    <section className={styles.symbolList}>
      {ranked.map((symbolId) => {
        const symbol = tapestry.symbols[symbolId];
        return (
          <div className={styles.symbolListRow} key={symbolId}>
            <div>
              <strong>{nameOf(symbolId)}</strong>
              <span>{symbol.role}</span>
            </div>
            <Chip size="small" label={symbol.count} />
            <p>{symbol.sentences[0]?.text || ""}</p>
          </div>
        );
      })}
    </section>
  );
}

Metric.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

TransportButton.propTypes = {
  title: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  children: PropTypes.node,
};

TapestryPanel.propTypes = {
  currentSegment: PropTypes.object,
  tapestry: PropTypes.object,
  threshold: PropTypes.number,
  nameOf: PropTypes.func,
};

TranscriptPanel.propTypes = {
  currentTurn: PropTypes.number,
  onSelect: PropTypes.func,
  segments: PropTypes.array,
  threshold: PropTypes.number,
  nameOf: PropTypes.func,
};

SymbolsPanel.propTypes = {
  tapestry: PropTypes.object,
  nameOf: PropTypes.func,
};

export default LiveTapPage;
